/**
 * Light, dark, or whatever the phone says.
 *
 * Three states, not two. `'system'` is not "resolve the phone's scheme now and
 * write that down" — it removes `data-theme` entirely and lets
 * `prefers-color-scheme` in `tokens.css` decide, which means the app follows
 * the phone live, including the automatic switch at dusk, with no listener and
 * nothing to keep in sync. D4 asked for dark mode following the phone; this is
 * what following means.
 *
 * That is also why the attribute is absent rather than set to `"system"`:
 * `tokens.css`'s media rule is guarded `:root:not([data-theme="light"])`, so
 * an explicit light choice beats a dark phone and an absent attribute defers
 * to it. One value, one job.
 */

export type Scheme = 'light' | 'dark'
export type ThemeChoice = Scheme | 'system'

/** Per-device, so it is a `localStorage` key and not a row in Postgres: Doug
 *  reading in bed on a phone and at a desk on a laptop are two answers. Never
 *  cleared — the same store holds the signed-in session. */
const KEY = 'sl-theme'

/** The paper each scheme paints, for the address bar and the task switcher.
 *  Kept in step with `--paper` in `tokens.css` by hand; a meta tag cannot read
 *  a custom property. */
const PAPER: Record<Scheme, string> = { light: '#FAFAF8', dark: '#121110' }

export function getTheme(): ThemeChoice {
  // A private window throws on access rather than returning null, and an
  // unreadable preference is not a reason to fail to render.
  try {
    const v = localStorage.getItem(KEY)
    if (v === 'light' || v === 'dark' || v === 'system') return v
  } catch { /* no store */ }
  return 'system'
}

export function setTheme(choice: ThemeChoice): void {
  try { localStorage.setItem(KEY, choice) } catch { /* no store */ }
  applyTheme(choice)
}

/** Put the choice on `<html>`. Safe to call before the first render, and
 *  called there, so no frame is painted in the wrong scheme. */
export function applyTheme(choice: ThemeChoice): void {
  const root = document.documentElement
  if (choice === 'system') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', choice)
  paintChrome(choice)
}

/**
 * The scheme in force right now, resolved. For anything that has to *say*
 * which one is on — a settings row's checkmark — rather than style itself,
 * which CSS already handles.
 */
export function currentScheme(choice: ThemeChoice = getTheme()): Scheme {
  if (choice !== 'system') return choice
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/**
 * Boot. `override` comes from `?theme=` in the URL (see `router.ts`) and is
 * applied **without being stored**: it is how the audit harness takes a dark
 * render of a screen, and a screenshot run must not leave a preference behind
 * on the device that took it.
 */
export function bootTheme(override: Scheme | null): void {
  applyTheme(override ?? getTheme())
}

/**
 * `theme-color`, which is the status bar in an installed PWA and the address
 * bar in Safari.
 *
 * Two metas live in `index.html`, one per scheme, so the chrome is right
 * before any script runs. They match on `prefers-color-scheme`, which is the
 * phone's answer and not necessarily the app's — so an explicit choice has to
 * retarget them, or a dark-mode phone holding a light-mode app would keep a
 * near-black status bar above a paper-white screen. The browser uses the first
 * meta whose media matches, and the light one is first in the document, so
 * `all` / `not all` is enough to pick either.
 */
function paintChrome(choice: ThemeChoice): void {
  const light = document.querySelector<HTMLMetaElement>('meta[name="theme-color"][data-scheme="light"]')
  const dark = document.querySelector<HTMLMetaElement>('meta[name="theme-color"][data-scheme="dark"]')
  if (!light || !dark) return
  if (choice === 'system') {
    light.media = '(prefers-color-scheme: light)'
    dark.media = '(prefers-color-scheme: dark)'
  } else {
    light.media = choice === 'light' ? 'all' : 'not all'
    dark.media = choice === 'dark' ? 'all' : 'not all'
  }
  // The content is fixed in the HTML; re-asserting it keeps the two copies of
  // each value in one place if a paper ever changes.
  light.content = PAPER.light
  dark.content = PAPER.dark
}
